import React from "react";
import { Col } from "react-bootstrap";
import { LookupVIewDto } from "../../../assets/utils/lib/lookupDto";

import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import { VfxLib } from "../../../assets/vfx";
import Control from "../../../assets/vfx/controls/control";
import { SearchDto, SearchOperator } from "../../../assets/utils/lib/searchDto";

import {
  defaultSearchQuery,
  GRID_ACTION_TYPES,
  Module,
} from "../../../assets/types/common";
import { Constants } from "../../../assets/utils/lib/constants";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import Form from "../../../assets/vfx/form/form";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { ViewProps } from "../props";
import { LookupValueService } from "./lookupValueService";
import { ControlDatagridActionButtonProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";

interface LookupValuePageProps<T> {
  controlStyles?: IControlStyles | undefined;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  searchValues?: SearchDto | undefined;
  moduleType: Module;
  lookupCategoryLookupValues?: DropdownOption[] | undefined;
}

export const LookupValuePage = <T extends ViewProps.ValueTypeDto>(
  props: LookupValuePageProps<T>
) => {
  const [searchQuery, setSearchQuery] =
    React.useState<SearchDto>(defaultSearchQuery);
  const service: LookupValueService<T> = new LookupValueService<T>(
    props.moduleType
  );
  const lookupValueGridQuery = service.gridQuery(searchQuery);
  const isGridLoading =
    lookupValueGridQuery.isLoading || lookupValueGridQuery.isFetching;
  const styles: IControlStyles = props.controlStyles ?? useControlStyles();

  const handleSearch = (values: {
    name: string | undefined;
    categoryId: string | undefined;
  }) => {
    const filters = [];

    if (values.name && values.name.trim() !== Constants.emptyString) {
      filters.push({
        field: "name",
        operator: SearchOperator.Contains,
        value: values.name,
      });
    }

    if (values.categoryId && values.categoryId != Constants.emptyGuid) {
      filters.push({
        field: "typeId",
        operator: SearchOperator.Equal,
        value: values.categoryId,
      });
    }

    if (filters.length > 0 && Array.isArray(defaultSearchQuery.filters)) {
      filters.push(...defaultSearchQuery.filters);
    }

    if (filters.length > 0) {
      setSearchQuery({
        ...defaultSearchQuery,
        filters,
      });
    }
  };

  function handleNew() {
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);
  }

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...defaultSearchQuery,
      sorts: prev.sorts,
      pageSize: prev.pageSize ?? 10,
    }));
  };

  function actionButtons(row: LookupVIewDto) {
    let actionButton: ControlDatagridActionButtonProps[] = [
      VfxLib.datagridActionButtons({ datagridStyles: styles }).edit,
      VfxLib.datagridActionButtons({ datagridStyles: styles }).view,
    ];
    if (row.isLocked && props.moduleType === Module.PopHub) {
      actionButton.splice(0, 1);
    }
    return actionButton;
  }

  const searchContainer: React.JSX.Element = (
    <SearchContainer
      initialValues={{
        name: Constants.emptyString,
        categoryId: Constants.emptyGuid,
      }}
      onSearch={handleSearch}
      onClear={handleClear}
      enableReinitialize
    >
      <Col lg={4}>
        <Form.Text name={"name"} label={"Name"} />
      </Col>
      <Col lg={4} className="ms-2">
        <Form.Dropdown
          name={"categoryId"}
          label={"Category"}
          blankOption={{
            key: Constants.emptyGuid,
            display: "--Select Category--",
            value: Constants.emptyGuid,
          }}
          options={props?.lookupCategoryLookupValues}
        />
      </Col>
    </SearchContainer>
  );

  return (
    <PageContent hideHeader>
        <Control.DataGrid
          dataId="id"
          height="55vh"
          cols={[
            {
              data: "id",
              title: "ID",
              visible: false,
              columnType: "number",
              alignment: "right",
              sortable: false,
            },
            {
              data: "name",
              title: "Name",
              visible: true,
              columnType: "text",
              alignment: "left",
              width: "18%",
            },
            {
              data: "description",
              title: "Description",
              visible: true,
              columnType: "text",
              alignment: "left",
              width: "24%",
            },
            {
              data: "typeId",
              title: "Category",
              visible: true,
              columnType: "custom",
              alignment: "left",
              width: "24%",
              customCell: (typeId: string) => {
                return props?.lookupCategoryLookupValues?.find(
                  (x) => x.value === typeId
                )?.display;
              },
            },
          ]}
          actionCol={{
            data: "id",
            title: "Action",
            visible: true,
            columnType: "action",
            alignment: "right",
            width: "100px",
            controlStyles: styles,
            actionButtons: (row) => actionButtons(row),
          }}
          onCustomEvent={props.onCustomEvent}
          data={lookupValueGridQuery?.data?.result ?? []}
          totalRecords={
            lookupValueGridQuery.data?.totalSize ??
            lookupValueGridQuery?.data?.result?.length ??
            0
          }
          query={searchQuery}
          loading={isGridLoading}
          onQueryChange={(patch) => {
            setSearchQuery((query) => ({
              ...query,
              ...patch,
              sorts: patch.sortBy ? [patch.sortBy] : query.sorts,
            }));
          }}
          uiOptions={{
            title: "Lookup Values",
            filterContent: searchContainer,
            primaryAction: {
              text: "Add Lookup Value",
              icon: "bi bi-plus-lg",
              onClick: handleNew,
            },
          }}
        />
    </PageContent>
  );
};
